import { createListenerMiddleware } from '@reduxjs/toolkit'
import { AppDispatch, RootState } from '@/store/store'
import { selectIncompleteUploadedFileIds } from '@/uppy-file-upload/store/selectors'
import { UserFilesPollingActions } from '@/user_files/polling/store/slice'
import { selectCurrentFolderId } from '@/user_files/folders/store/selectors'
import { UserFilesActions } from './slice'
import { selectCurrentFolderProcessingFileIds, selectLoadedUserFilesById } from './selectors'
import { getFolderFiles } from '../fetch/get-folder-files'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'
import { fetchAndStoreUserFiles } from '../utils/fetch-and-store-user-files'
import { ApiError } from '@/util/errors/api-error'
import { UserFoldersActions } from '@/user_files/folders/store/slice'

export const userFilesListenerMiddleware = createListenerMiddleware()
const startListener = userFilesListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()
const inFlightFolderFileLoads = new Set<string>()
const queuedFolderFileRefreshes = new Set<string>()

function isMissingOrDeniedError(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 403 || error.status === 404)
}

// ============================================================================
// Fetch Files for Folder
// ============================================================================

startListener({
  actionCreator: UserFilesActions.fetchFilesForFolder,
  effect: async (action, listenerApi) => {
    const { folderId } = action.payload
    if (inFlightFolderFileLoads.has(folderId)) {
      queuedFolderFileRefreshes.add(folderId)
      return
    }

    inFlightFolderFileLoads.add(folderId)
    listenerApi.dispatch(UserFilesActions.fetchFilesStarted({ folderId }))

    try {
      const response = await getFolderFiles(folderId, {
        statuses: [UserFileProcessingStatus.Processing, UserFileProcessingStatus.Completed, UserFileProcessingStatus.Failed],
      })
      listenerApi.dispatch(
        UserFilesActions.fetchFilesSucceeded({
          folderId,
          files: response.files,
        })
      )
    } catch (error) {
      console.error('Failed to fetch folder files:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch files'
      const statusCode = error instanceof ApiError ? error.status : undefined
      if (isMissingOrDeniedError(error)) {
        listenerApi.dispatch(UserFilesActions.clearFolderFiles({ folderId }))
        if (selectCurrentFolderId(listenerApi.getState()) === folderId) {
          listenerApi.dispatch(UserFoldersActions.urlInitializationFailed({ folderId, error: errorMessage, statusCode: error.status }))
        }
      }
      listenerApi.dispatch(UserFilesActions.fetchFilesFailed({ folderId, error: errorMessage, statusCode }))
    } finally {
      inFlightFolderFileLoads.delete(folderId)

      if (queuedFolderFileRefreshes.delete(folderId)) {
        listenerApi.dispatch(UserFilesActions.fetchFilesForFolder({ folderId }))
      }
    }
  },
})

startListener({
  actionCreator: UserFilesPollingActions.pollBatchReceived,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()
    const currentFolderId = selectCurrentFolderId(state)

    const currentFolderFileIds = new Set<string>(currentFolderId ? state.userFilesState.filesByFolderId[currentFolderId] ?? [] : [])
    const currentFolderProcessingIds = new Set(selectCurrentFolderProcessingFileIds(state))
    const drawerFileIds = new Set(selectIncompleteUploadedFileIds(state))
    const isCurrentFolderLoaded = currentFolderId ? state.userFilesState.fileListRequestsByFolderId[currentFolderId]?.status === 'success' : false

    const returnedRecords = Object.values(action.payload.files).map((fileData) => fileData.record)

    // Update records that belong to a loaded folder.
    const recordsToUpdate = returnedRecords.filter((record) => state.userFilesState.filesById[record.id])
    if (recordsToUpdate.length > 0) {
      listenerApi.dispatch(UserFilesActions.upsertUserFiles(recordsToUpdate))
    }

    const returnedIds = new Set(Object.keys(action.payload.files))
    const hasMissingCurrentFolderFiles =
      !!currentFolderId && action.payload.requestedIds.some((requestedId) => currentFolderProcessingIds.has(requestedId) && !returnedIds.has(requestedId))

    const shouldRefreshCurrentFolder = returnedRecords.some((record) => {
      if (!currentFolderId) return false
      if (!drawerFileIds.has(record.id)) return false
      if (record.processing_status === UserFileProcessingStatus.Pending) return false
      if (record.folder_id !== currentFolderId) return false
      if (!isCurrentFolderLoaded) return false
      if (currentFolderFileIds.has(record.id)) return false
      return true
    })

    if (currentFolderId && (hasMissingCurrentFolderFiles || shouldRefreshCurrentFolder)) {
      listenerApi.dispatch(UserFilesActions.fetchFilesForFolder({ folderId: currentFolderId }))
    }
  },
})

// ============================================================================
// Prefetch selected files on conversation load
// ============================================================================

// When a conversation restores its selected user files, load them into the store so the
// selected-file chips render their names (and open) without a fetch when expanded.
// Best-effort: failures are left for the chips' own fetch to retry on open.
startListener({
  actionCreator: AgentConversationFormActions.restoreRequestParams,
  effect: async (action, listenerApi) => {
    const ids = action.payload.requestParams.user_files_rag?.user_file_ids ?? []
    if (ids.length === 0) return

    const loaded = selectLoadedUserFilesById(listenerApi.getState())
    const missing = [...new Set(ids)].filter((id) => !loaded[id])
    if (missing.length === 0) return

    try {
      await fetchAndStoreUserFiles(missing, listenerApi.dispatch)
    } catch (error) {
      console.error('Failed to prefetch selected user files:', error)
    }
  },
})
